import React, { useState, useEffect } from 'react';
import { ArrowLeft, Rocket, Lock, Unlock, Star, Trophy, HelpCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import successSound from '../assets/sounds/success.wav';
import errorSound from '../assets/sounds/error2.mp3';

interface SpaceFractionGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

interface Zone {
  id: number;
  name: string;
  theme: string;
  unlocked: boolean;
  completed: boolean;
  challenges: Challenge[];
}

interface Challenge {
  id: number;
  type: 'compare' | 'equivalent' | 'add_subtract' | 'simplify' | 'mixed';
  question: string;
  options: string[];
  correctAnswer: string;
  explanation: string;
}

const SpaceFractionGame: React.FC<SpaceFractionGameProps> = ({ mode, onReset, language }) => {
  const [currentZone, setCurrentZone] = useState<number | null>(null);
  const [currentChallenge, setCurrentChallenge] = useState<number>(0);
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const [playSuccess] = useSound(successSound, { volume: 0.5 });
  const [playError] = useSound(errorSound, { volume: 0.3 });

  const tutorialSteps = [
    {
      title: language === 'en' ? 'Welcome to Space Fractions!' : 'Bienvenue dans les Fractions Spatiales !',
      content: language === 'en' 
        ? 'Navigate through space zones by solving fraction challenges! Each zone tests different skills.'
        : 'Naviguez à travers les zones spatiales en résolvant des défis de fractions ! Chaque zone teste différentes compétences.'
    },
    {
      title: language === 'en' ? 'Zone Progression' : 'Progression des zones',
      content: language === 'en'
        ? 'Complete all challenges in a zone to unlock the next one. Master all 5 zones to complete your mission!'
        : 'Complétez tous les défis d\'une zone pour débloquer la suivante. Maîtrisez les 5 zones pour terminer votre mission !'
    },
    {
      title: language === 'en' ? 'Ready for Launch!' : 'Prêt pour le décollage !',
      content: language === 'en'
        ? 'Your spaceship is ready! Start with Zone 1 and work your way through the galaxy.'
        : 'Votre vaisseau spatial est prêt ! Commencez par la Zone 1 et traversez la galaxie.'
    }
  ];

  const initializeZones = (): Zone[] => {
    return [
      {
        id: 1,
        name: language === 'en' ? 'Comparison Station' : 'Station de Comparaison',
        theme: 'compare',
        unlocked: true,
        completed: false,
        challenges: generateChallenges('compare', 5)
      },
      {
        id: 2,
        name: language === 'en' ? 'Equivalent Nebula' : 'Nébuleuse Équivalente',
        theme: 'equivalent',
        unlocked: false,
        completed: false,
        challenges: generateChallenges('equivalent', 5)
      },
      {
        id: 3,
        name: language === 'en' ? 'Addition Asteroid' : 'Astéroïde Addition',
        theme: 'add_subtract',
        unlocked: false,
        completed: false,
        challenges: generateChallenges('add_subtract', 5)
      },
      {
        id: 4,
        name: language === 'en' ? 'Simplification Solar System' : 'Système Solaire Simplification',
        theme: 'simplify',
        unlocked: false,
        completed: false,
        challenges: generateChallenges('simplify', 5)
      },
      {
        id: 5,
        name: language === 'en' ? 'Final Mission' : 'Mission Finale',
        theme: 'mixed',
        unlocked: false,
        completed: false,
        challenges: generateChallenges('mixed', 8)
      }
    ];
  };

  const generateChallenges = (type: Challenge['type'], count: number): Challenge[] => {
    const challenges: Challenge[] = [];
    
    for (let i = 0; i < count; i++) {
      challenges.push(generateChallenge(type, i + 1));
    }
    
    return challenges;
  };

  const generateChallenge = (type: Challenge['type'], index: number): Challenge => {
    switch (type) {
      case 'compare':
        return generateCompareChallenge(index);
      case 'equivalent':
        return generateEquivalentChallenge(index);
      case 'add_subtract':
        return generateAddSubtractChallenge(index);
      case 'simplify':
        return generateSimplifyChallenge(index);
      case 'mixed':
        const types: Challenge['type'][] = ['compare', 'equivalent', 'add_subtract', 'simplify'];
        const randomType = types[Math.floor(Math.random() * types.length)];
        return generateChallenge(randomType, index);
      default:
        return generateCompareChallenge(index);
    }
  };

  const generateCompareChallenge = (index: number): Challenge => {
    const fractions = [
      { num: Math.floor(Math.random() * 5) + 1, den: Math.floor(Math.random() * 6) + 2 },
      { num: Math.floor(Math.random() * 5) + 1, den: Math.floor(Math.random() * 6) + 2 }
    ];
    
    const value1 = fractions[0].num / fractions[0].den;
    const value2 = fractions[1].num / fractions[1].den;
    
    let correctAnswer: string;
    if (Math.abs(value1 - value2) < 0.001) {
      correctAnswer = language === 'en' ? 'Equal' : 'Égal';
    } else if (value1 > value2) {
      correctAnswer = `${fractions[0].num}/${fractions[0].den}`;
    } else {
      correctAnswer = `${fractions[1].num}/${fractions[1].den}`;
    }
    
    return {
      id: index,
      type: 'compare',
      question: language === 'en' 
        ? `Which fraction is larger: ${fractions[0].num}/${fractions[0].den} or ${fractions[1].num}/${fractions[1].den}?`
        : `Quelle fraction est plus grande : ${fractions[0].num}/${fractions[0].den} ou ${fractions[1].num}/${fractions[1].den} ?`,
      options: [
        `${fractions[0].num}/${fractions[0].den}`,
        `${fractions[1].num}/${fractions[1].den}`,
        language === 'en' ? 'Equal' : 'Égal'
      ],
      correctAnswer,
      explanation: language === 'en'
        ? `${fractions[0].num}/${fractions[0].den} = ${value1.toFixed(3)}, ${fractions[1].num}/${fractions[1].den} = ${value2.toFixed(3)}`
        : `${fractions[0].num}/${fractions[0].den} = ${value1.toFixed(3)}, ${fractions[1].num}/${fractions[1].den} = ${value2.toFixed(3)}`
    };
  };

  const generateEquivalentChallenge = (index: number): Challenge => {
    const baseFraction = { num: Math.floor(Math.random() * 4) + 1, den: Math.floor(Math.random() * 4) + 2 };
    const multiplier = Math.floor(Math.random() * 3) + 2;
    const equivalent = { num: baseFraction.num * multiplier, den: baseFraction.den * multiplier };
    
    const wrongOptions = [
      { num: baseFraction.num + 1, den: baseFraction.den + 1 },
      { num: baseFraction.num * 2, den: baseFraction.den * 3 },
      { num: baseFraction.num + multiplier, den: baseFraction.den + multiplier }
    ];
    
    const options = [
      `${equivalent.num}/${equivalent.den}`,
      ...wrongOptions.map(f => `${f.num}/${f.den}`)
    ].sort(() => Math.random() - 0.5);
    
    return {
      id: index,
      type: 'equivalent',
      question: language === 'en'
        ? `Which fraction is equivalent to ${baseFraction.num}/${baseFraction.den}?`
        : `Quelle fraction est équivalente à ${baseFraction.num}/${baseFraction.den} ?`,
      options,
      correctAnswer: `${equivalent.num}/${equivalent.den}`,
      explanation: language === 'en'
        ? `${baseFraction.num}/${baseFraction.den} × ${multiplier}/${multiplier} = ${equivalent.num}/${equivalent.den}`
        : `${baseFraction.num}/${baseFraction.den} × ${multiplier}/${multiplier} = ${equivalent.num}/${equivalent.den}`
    };
  };

  const generateAddSubtractChallenge = (index: number): Challenge => {
    const isAddition = Math.random() > 0.5;
    const denominator = [2, 3, 4, 6, 8][Math.floor(Math.random() * 5)];
    
    let frac1 = { num: Math.floor(Math.random() * (denominator - 1)) + 1, den: denominator };
    let frac2 = { num: Math.floor(Math.random() * (denominator - 1)) + 1, den: denominator };
    
    if (!isAddition && frac1.num < frac2.num) {
      [frac1, frac2] = [frac2, frac1];
    }
    
    const resultNum = isAddition ? frac1.num + frac2.num : frac1.num - frac2.num;
    const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
    const divisor = gcd(resultNum, denominator);
    const simplifiedResult = { num: resultNum / divisor, den: denominator / divisor };
    
    const wrongOptions = [
      `${resultNum}/${denominator}`,
      `${resultNum + 1}/${denominator}`,
      `${resultNum}/${denominator + 1}`
    ].filter(opt => opt !== `${simplifiedResult.num}/${simplifiedResult.den}`);
    
    const options = [
      `${simplifiedResult.num}/${simplifiedResult.den}`,
      ...wrongOptions.slice(0, 3)
    ].sort(() => Math.random() - 0.5);
    
    return {
      id: index,
      type: 'add_subtract',
      question: language === 'en'
        ? `${frac1.num}/${frac1.den} ${isAddition ? '+' : '-'} ${frac2.num}/${frac2.den} = ?`
        : `${frac1.num}/${frac1.den} ${isAddition ? '+' : '-'} ${frac2.num}/${frac2.den} = ?`,
      options,
      correctAnswer: `${simplifiedResult.num}/${simplifiedResult.den}`,
      explanation: language === 'en'
        ? `${frac1.num}/${frac1.den} ${isAddition ? '+' : '-'} ${frac2.num}/${frac2.den} = ${resultNum}/${denominator} = ${simplifiedResult.num}/${simplifiedResult.den}`
        : `${frac1.num}/${frac1.den} ${isAddition ? '+' : '-'} ${frac2.num}/${frac2.den} = ${resultNum}/${denominator} = ${simplifiedResult.num}/${simplifiedResult.den}`
    };
  };

  const generateSimplifyChallenge = (index: number): Challenge => {
    const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
    
    let simplifiedNum, simplifiedDen;
    do {
      simplifiedNum = Math.floor(Math.random() * 5) + 1;
      simplifiedDen = Math.floor(Math.random() * 5) + 2;
    } while (gcd(simplifiedNum, simplifiedDen) !== 1);
    
    const multiplier = Math.floor(Math.random() * 4) + 2;
    const originalNum = simplifiedNum * multiplier;
    const originalDen = simplifiedDen * multiplier;
    
    const wrongOptions = [
      `${originalNum}/${originalDen}`,
      `${simplifiedNum + 1}/${simplifiedDen}`,
      `${simplifiedNum}/${simplifiedDen + 1}`
    ].filter(opt => opt !== `${simplifiedNum}/${simplifiedDen}`);
    
    const options = [
      `${simplifiedNum}/${simplifiedDen}`,
      ...wrongOptions.slice(0, 3)
    ].sort(() => Math.random() - 0.5);
    
    return {
      id: index,
      type: 'simplify',
      question: language === 'en'
        ? `Simplify ${originalNum}/${originalDen} to its lowest terms:`
        : `Simplifiez ${originalNum}/${originalDen} à sa forme la plus simple :`,
      options,
      correctAnswer: `${simplifiedNum}/${simplifiedDen}`,
      explanation: language === 'en'
        ? `${originalNum}/${originalDen} ÷ ${multiplier}/${multiplier} = ${simplifiedNum}/${simplifiedDen}`
        : `${originalNum}/${originalDen} ÷ ${multiplier}/${multiplier} = ${simplifiedNum}/${simplifiedDen}`
    };
  };

  const handleAnswer = (answer: string) => {
    if (!currentZone || showFeedback) return;
    
    setSelectedAnswer(answer);
    setShowFeedback(true);
    
    const zone = zones[currentZone - 1];
    const challenge = zone.challenges[currentChallenge];
    const correct = answer === challenge.correctAnswer;
    
    setIsCorrect(correct);
    
    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);
    
    if (correct) {
      playSuccess();
    } else {
      playError();
    }
    
    setTimeout(() => {
      if (correct) {
        if (currentChallenge < zone.challenges.length - 1) {
          setCurrentChallenge(currentChallenge + 1);
        } else {
          // Zone completed
          const updatedZones = zones.map(z => {
            if (z.id === currentZone) {
              return { ...z, completed: true };
            } else if (z.id === currentZone + 1) {
              return { ...z, unlocked: true };
            }
            return z;
          });
          
          setZones(updatedZones);
          
          if (currentZone === 5) {
            setGameComplete(true);
            saveProgress(newStats);
          } else {
            setCurrentZone(null);
          }
        }
      }
      
      setShowFeedback(false);
      setSelectedAnswer('');
    }, 2000);
  };

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'fractions',
        mode: 'space',
        questions_answered: currentStats.questionsAnswered,
        correct_answers: currentStats.correctAnswers,
        average_response_time: 0,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const resetGame = () => {
    setZones(initializeZones());
    setCurrentZone(null);
    setCurrentChallenge(0);
    setGameComplete(false);
    setShowTutorial(true);
    setTutorialStep(0);
    setStats({
      questionsAnswered: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
      totalTime: 0,
      averageResponseTime: 0,
    });
  };

  useEffect(() => {
    if (!showTutorial) {
      setZones(initializeZones());
    }
  }, [showTutorial, language]);

  if (showTutorial) {
    return (
      <div className="w-full max-w-4xl">
        <div className="mb-6 flex items-center">
          <button
            onClick={onReset}
            className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
          </button>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Rocket className="h-16 w-16 text-purple-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {tutorialSteps[tutorialStep].title}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
            {tutorialSteps[tutorialStep].content}
          </p>
          
          <div className="flex justify-center gap-4">
            {tutorialStep > 0 && (
              <button
                onClick={() => setTutorialStep(tutorialStep - 1)}
                className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
              >
                {language === 'en' ? 'Previous' : 'Précédent'}
              </button>
            )}
            
            {tutorialStep < tutorialSteps.length - 1 ? (
              <button
                onClick={() => setTutorialStep(tutorialStep + 1)}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                {language === 'en' ? 'Next' : 'Suivant'}
              </button>
            ) : (
              <button
                onClick={() => setShowTutorial(false)}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {language === 'en' ? 'Launch Mission!' : 'Lancer la mission !'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowTutorial(true)}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
        </div>
      </div>

      {gameComplete ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Mission Accomplished!' : 'Mission accomplie !'}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {stats.correctAnswers}/{stats.questionsAnswered}
              </div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Challenges Solved' : 'Défis résolus'}
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {Math.round((stats.correctAnswers / stats.questionsAnswered) * 100)}%
              </div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Success Rate' : 'Taux de réussite'}
              </div>
            </div>
          </div>
          <button
            onClick={resetGame}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            {language === 'en' ? 'New Mission' : 'Nouvelle mission'}
          </button>
        </div>
      ) : currentZone ? (
        // Challenge View
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setCurrentZone(null)}
              className="flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
            >
              <ArrowLeft className="h-5 w-5 mr-1" />
              {language === 'en' ? 'Back to Galaxy Map' : 'Retour à la carte galactique'}
            </button>
            
            <div className="text-lg font-medium text-purple-700 dark:text-purple-300">
              {zones[currentZone - 1]?.name} - {language === 'en' ? 'Challenge' : 'Défi'} {currentChallenge + 1}/{zones[currentZone - 1]?.challenges.length}
            </div>
          </div>

          {zones[currentZone - 1]?.challenges[currentChallenge] && (
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                  {zones[currentZone - 1].challenges[currentChallenge].question}
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {zones[currentZone - 1].challenges[currentChallenge].options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(option)}
                    disabled={showFeedback}
                    className={`p-4 rounded-lg border-2 transition-colors text-lg font-medium ${
                      showFeedback
                        ? option === zones[currentZone - 1].challenges[currentChallenge].correctAnswer
                          ? 'border-green-500 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                          : option === selectedAnswer
                          ? 'border-red-500 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                          : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700'
                        : 'border-slate-200 dark:border-slate-700 hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/30'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>

              {showFeedback && (
                <div className="text-center">
                  <div className={`inline-block p-4 rounded-lg ${
                    isCorrect 
                      ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                  }`}>
                    <p className="font-medium mb-2">
                      {isCorrect 
                        ? (language === 'en' ? 'Correct!' : 'Correct !')
                        : (language === 'en' ? 'Incorrect!' : 'Incorrect !')}
                    </p>
                    <p className="text-sm">
                      {zones[currentZone - 1].challenges[currentChallenge].explanation}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        // Galaxy Map View
        <div className="bg-gradient-to-b from-purple-900 to-black rounded-xl shadow-md p-8 min-h-[600px] relative overflow-hidden">
          {/* Stars background */}
          <div className="absolute inset-0">
            {Array.from({ length: 50 }).map((_, i) => (
              <Star
                key={i}
                className="absolute text-white opacity-30"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  fontSize: `${Math.random() * 8 + 4}px`
                }}
              />
            ))}
          </div>

          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              {language === 'en' ? 'Galaxy Map' : 'Carte Galactique'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {zones.map((zone) => (
                <div
                  key={zone.id}
                  className={`relative p-6 rounded-xl border-2 transition-all ${
                    zone.completed
                      ? 'border-green-500 bg-green-900/30 text-green-100'
                      : zone.unlocked
                      ? 'border-purple-500 bg-purple-900/30 text-purple-100 hover:bg-purple-800/40 cursor-pointer'
                      : 'border-slate-600 bg-slate-800/30 text-slate-400'
                  }`}
                  onClick={() => zone.unlocked && !zone.completed && setCurrentZone(zone.id)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">{zone.name}</h3>
                    {zone.completed ? (
                      <Unlock className="h-6 w-6 text-green-400" />
                    ) : zone.unlocked ? (
                      <Rocket className="h-6 w-6 text-purple-400" />
                    ) : (
                      <Lock className="h-6 w-6 text-slate-500" />
                    )}
                  </div>
                  
                  <div className="text-sm mb-4">
                    {language === 'en' ? 'Challenges:' : 'Défis :'} {zone.challenges.length}
                  </div>
                  
                  <div className="text-xs">
                    {zone.completed
                      ? (language === 'en' ? 'Completed!' : 'Terminé !')
                      : zone.unlocked
                      ? (language === 'en' ? 'Click to enter' : 'Cliquez pour entrer')
                      : (language === 'en' ? 'Locked' : 'Verrouillé')}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <div className="inline-block bg-black/50 rounded-lg p-4">
                <div className="text-white text-sm">
                  {language === 'en' ? 'Progress:' : 'Progression :'} {zones.filter(z => z.completed).length}/5 {language === 'en' ? 'zones completed' : 'zones terminées'}
                </div>
                <div className="w-64 h-2 bg-slate-700 rounded-full mt-2 mx-auto">
                  <div 
                    className="h-full bg-purple-500 rounded-full transition-all duration-500"
                    style={{ width: `${(zones.filter(z => z.completed).length / 5) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpaceFractionGame;